import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { ApiFehler, fehlerText } from "../api/client";

/** Leerzustand (Handoff Schiefer): Kachel 64 in Akzent-Toenung mit Lucide-Symbol (nie die Glyph als
 *  Dekoration), Titel 20/700, kurzer Klartext 15 in --pg-text-2, darunter hoechstens ein primaerer und
 *  ein sekundaerer Knopf. W73c (D-286 H2): `zusatz` entfernt, seit W73 steht der KI-Satz nicht mehr im Leerzustand. */
export function Leer(props: { icon: LucideIcon; titel: string; children?: ReactNode; aktion?: ReactNode }) {
  const Icon = props.icon;
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="leer-kachel grid size-(--leer-kachel) place-items-center rounded-lg bg-pg-accent-soft text-pg-accent-text">
        <Icon size={28} strokeWidth={1.5} aria-hidden />
      </span>
      <p className="leer-titel t-title-2 mt-4">{props.titel}</p>
      {props.children && <p className="t-subheadline mt-1 max-w-(--w-readable) text-pg-text-2">{props.children}</p>}
      {props.aktion && <div className="mt-6 flex flex-wrap justify-center gap-3">{props.aktion}</div>}
    </div>
  );
}

/** Ladezustand ohne Springen: ruhige Zeile statt Spinner-Seite. */
export function Laedt(props: { text?: string }) {
  return (
    <p className="t-subheadline px-4 py-6 text-pg-text-2" aria-busy>
      {props.text ?? "Wird geladen …"}
    </p>
  );
}

/** Fehler als Klartext; bei einer erreichten Plan-Grenze (402) mit Weg zur Plan-Seite (W9). */
export function Fehlertext(props: { fehler: unknown }) {
  // nie der Text des Browsers („Failed to fetch“, Safari „Load failed“): ein TypeError der Anfrage heisst ohne Netz der Satz der App
  // W82 Web-5f (BS30): ebenso der Abbruch nach der Zeitgrenze des Sicherns (`useSichern`, TimeoutError)
  const ohneNetz = props.fehler instanceof TypeError || (props.fehler instanceof DOMException && props.fehler.name === "TimeoutError");
  const text = ohneNetz ? fehlerText(props.fehler) : props.fehler instanceof Error ? props.fehler.message : "Unbekannter Fehler";
  return (
    <p role="alert" className="t-subheadline px-4 py-3 text-pg-danger">
      {text}
      {props.fehler instanceof ApiFehler && props.fehler.status === 402 && (
        <>
          {" "}
          <Link to="/plan" className="inline-flex min-h-(--touch) items-center text-pg-accent-text hover:opacity-70">
            Plan ansehen
          </Link>
        </>
      )}
    </p>
  );
}

/** „Weitere laden“ unter einer Liste, mit Stand (Verifier W4, F13: sonst still bei 100/200 Schluss). */
export function WeitereLaden(props: { geladen: number; gesamt: number; laedt: boolean; onLaden: () => void }) {
  if (props.geladen >= props.gesamt) return null;
  return (
    <div className="mt-4 flex flex-col items-center gap-1">
      <button type="button" onClick={props.onLaden} disabled={props.laedt} className="t-body min-h-(--touch) px-4 text-pg-accent-text hover:opacity-70 disabled:opacity-40">
        {props.laedt ? "Lädt …" : "Weitere laden"}
      </button>
      <p className="t-footnote t-zahl text-pg-text-2">
        {props.geladen} von {props.gesamt}
      </p>
    </div>
  );
}
